// app/src/dashboard/widgets/chart/useTradeMarkers.ts
import { useEffect, useRef } from "react";
import { createSeriesMarkers, ISeriesApi, ISeriesMarkersPluginApi, SeriesMarker, Time } from "lightweight-charts";
import { chartColors } from "./chartColors";

interface TradeForMarker {
  symbol: string;
  type: "long" | "short";
  entryPrice: number;
  exitPrice: number | null;
  date: Date | string | null;       // entry date (Prisma field name)
  exitDate: Date | string | null;
  status: string;
}

function toSeconds(d: Date | string | null): number | null {
  if (!d) return null;
  const ms = d instanceof Date ? d.getTime() : new Date(d).getTime();
  return isNaN(ms) ? null : Math.floor(ms / 1000);
}

function snapToCandle(targetSec: number, candleTimes: number[]): number | null {
  if (candleTimes.length === 0) return null;
  // After last candle → snap to last
  if (targetSec > candleTimes[candleTimes.length - 1]) return candleTimes[candleTimes.length - 1];
  // Before first candle → snap to first
  if (targetSec < candleTimes[0]) return candleTimes[0];
  for (let i = candleTimes.length - 1; i >= 0; i--) {
    if (candleTimes[i] <= targetSec) return candleTimes[i];
  }
  return candleTimes[0];
}

function buildMarkers(
  trades: TradeForMarker[],
  widgetSymbol: string,
  candleTimes: number[],
  showExit: boolean
): SeriesMarker<Time>[] {
  // Normalize: strip =X suffix, extract root from futures contracts (MNQM6 → mnq, NQ1! → nq)
  function normSym(s: string): string {
    const u = s.toUpperCase().replace(/=X$/i, "");
    if (u.endsWith("!")) return u.replace(/\d+!$/, "").toLowerCase();
    const m = u.match(/^([A-Z]{1,6})[FGHJKMNQUVXZ]\d{1,2}$/);
    return (m ? m[1] : u).toLowerCase();
  }
  const normalizedWidget = normSym(widgetSymbol);
  // Marker colors from per-theme tokens. Buy (long entry) and sell (short entry)
  // get distinct semantic colors so they're instantly distinguishable; exit is
  // a third, separate color. Each theme defines these in client/Main.css.
  const cc = chartColors();
  const buyColor = cc.markerBuy || "#22c55e";
  const sellColor = cc.markerSell || "#ef4444";
  const markers: SeriesMarker<Time>[] = [];

  for (const trade of trades) {
    const tradeSymbol = normSym(trade.symbol ?? "");
    if (tradeSymbol !== normalizedWidget) continue;

    const entrySec = toSeconds(trade.date);
    if (!entrySec) continue;

    const entryTime = snapToCandle(entrySec, candleTimes);
    if (!entryTime) continue;

    // Entry marker encodes direction by color + arrow shape:
    // long (buy) = arrowUp purple, short (sell) = arrowDown amber.
    markers.push({
      time: entryTime as Time,
      position: trade.type === "long" ? "atPriceBottom" : "atPriceTop",
      price: Number(trade.entryPrice),
      shape: trade.type === "long" ? "arrowUp" : "arrowDown",
      color: trade.type === "long" ? buyColor : sellColor,
      size: 0.75,
    });

    // Exit marker — only on single-trade views (showExit). An arrow pointing
    // OPPOSITE to the entry, with its tip touching the exit price. The exit is
    // the opposite side of the trade from the entry, so it uses the opposite
    // buy/sell color (same convention as everywhere else in the app: buy =
    // purple, sell = amber). long: entry=buy(purple)/exit=sell(amber);
    // short: entry=sell(amber)/exit=buy(purple). On the multi-trade dashboard
    // widget exit markers are off (showExit=false) to avoid clutter.
    if (showExit && trade.status === "closed" && trade.exitPrice != null) {
      const exitSec = toSeconds(trade.exitDate);
      if (exitSec) {
        const exitTime = snapToCandle(exitSec, candleTimes);
        if (exitTime) {
          // Opposite direction from the entry. Pairing shape+position so the
          // arrow tip points AT the price:
          //   arrowDown + atPriceTop    → above price, tip pointing down
          //   arrowUp   + atPriceBottom → below price, tip pointing up
          markers.push({
            time: exitTime as Time,
            position: trade.type === "long" ? "atPriceTop" : "atPriceBottom",
            price: Number(trade.exitPrice),
            shape: trade.type === "long" ? "arrowDown" : "arrowUp",
            color: trade.type === "long" ? sellColor : buyColor,
            size: 0.75,
          });
        }
      }
    }
  }

  markers.sort((a, b) => (a.time as number) - (b.time as number));
  return markers;
}

export function useTradeMarkers(
  seriesRef: React.RefObject<ISeriesApi<"Candlestick"> | null>,
  trades: TradeForMarker[],
  widgetSymbol: string,
  showTrades: boolean,
  candleTimes: number[],
  seriesVersion: number,
  showExit: boolean = false
) {
  const pluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const pluginSeriesVersionRef = useRef<number>(-1);

  useEffect(() => {
    const id = setTimeout(() => {
      const series = seriesRef.current;
      if (!series) return;

      const markers = showTrades && candleTimes.length > 0
        ? buildMarkers(trades, widgetSymbol, candleTimes, showExit)
        : [];

      // The series may have been disposed between scheduling and now (fast
      // widget remount/resize). Every plugin op below touches the underlying
      // chart, so wrap them: a disposed series throws "Object is disposed".
      try {
        // If series was recreated, detach stale plugin and create a fresh one
        if (pluginRef.current && pluginSeriesVersionRef.current !== seriesVersion) {
          try { pluginRef.current.detach(); } catch {}
          pluginRef.current = null;
        }

        if (pluginRef.current) {
          pluginRef.current.setMarkers(markers);
        } else {
          pluginRef.current = createSeriesMarkers(series, markers);
          pluginSeriesVersionRef.current = seriesVersion;
        }
      } catch {
        // series/chart disposed mid-flight — drop the stale plugin, no-op
        pluginRef.current = null;
      }
    }, 0);

    return () => clearTimeout(id);
  }, [seriesRef, trades, widgetSymbol, showTrades, candleTimes, seriesVersion, showExit]);

  // Detach on unmount
  useEffect(() => {
    return () => {
      if (pluginRef.current) {
        try { pluginRef.current.detach(); } catch {}
        pluginRef.current = null;
      }
    };
  }, []);
}
